import React, { useState, useEffect } from 'react';
import apiService, { uploadsBaseUrl } from '../api/apiService';
import './TablePages.css';

const resolveImageSrc = (url: string | undefined) =>
  url && !url.startsWith('http') ? `${uploadsBaseUrl}${url.startsWith('/') ? url : '/' + url}` : url;

interface BrandRow {
  id: number;
  name: string;
  logo_url?: string;
  is_active: boolean;
}

const Brands: React.FC = () => {
  const [list, setList] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formLogoUrl, setFormLogoUrl] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get('/admin/brands');
      setList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setFormName('');
    setFormLogoUrl('');
    setFormActive(true);
    setShowForm(true);
  };

  const openEdit = (row: BrandRow) => {
    setEditingId(row.id);
    setFormName(row.name);
    setFormLogoUrl(row.logo_url || '');
    setFormActive(row.is_active);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      alert('Enter brand name.');
      return;
    }
    try {
      const payload = { name: formName.trim(), logo_url: formLogoUrl.trim() || undefined, is_active: formActive };
      if (editingId) {
        await apiService.put(`/admin/brands/${editingId}`, payload);
      } else {
        await apiService.post('/admin/brands', payload);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await apiService.delete(`/admin/brands/${id}`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image (JPEG, PNG, GIF, WebP).');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiService.post('/upload/profile-image', formData, {
        transformRequest: [(data: unknown, headers: Record<string, string>) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
      const relativeUrl = (data && (data as { relative_url?: string }).relative_url) ? (data as { relative_url: string }).relative_url : '';
      const url = (data && (data as { url?: string }).url) ? (data as { url: string }).url : '';
      if (relativeUrl) setFormLogoUrl(relativeUrl);
      else if (url) setFormLogoUrl(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  return (
    <div className="table-page">
      <div className="table-toolbar">
        <div className="table-toolbar-right">
          <button type="button" className="btn-primary" onClick={openAdd}>Add brand</button>
          <button type="button" onClick={load} className="btn-primary">Refresh</button>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Logo URL</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    {row.logo_url ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <img
                          src={resolveImageSrc(row.logo_url)}
                          alt=""
                          style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain', borderRadius: 4, verticalAlign: 'middle' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <a href={resolveImageSrc(row.logo_url)} target="_blank" rel="noopener noreferrer">View</a>
                      </span>
                    ) : '-'}
                  </td>
                  <td>{row.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-sm" onClick={() => openEdit(row)}>Edit</button>
                      <button type="button" className="btn-sm red" onClick={() => handleDelete(row.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Edit' : 'Add'} brand</h3>
            <form onSubmit={handleSubmit}>
              <label>Brand name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="input-field"
                required
              />
              <label>Brand logo</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleUploadLogo} style={{ display: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo}>
                    {uploadingLogo ? 'Uploading...' : 'Upload image'}
                  </button>
                  {formLogoUrl && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <img src={resolveImageSrc(formLogoUrl)} alt="" style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain', borderRadius: 4 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <button type="button" className="btn-sm" onClick={() => setFormLogoUrl('')}>Remove</button>
                    </span>
                  )}
                </div>
                <input
                  type="url"
                  value={formLogoUrl}
                  onChange={(e) => setFormLogoUrl(e.target.value)}
                  className="input-field"
                  placeholder="Or paste image URL"
                />
              </div>
              <label>
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
                Active
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;
